import {useNavigate, useParams, Link} from "react-router-dom";
import {useForm, Controller} from "react-hook-form";
import React, {useState, useEffect, useContext, forwardRef} from "react";
import { ClassNames, css } from "@emotion/react";
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  HStack,
  VStack,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Input, Divider,
  Textarea, Alert, AlertIcon, AlertTitle, Select,
  Checkbox,
  InputProps,
  Text,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  AlertDialog, AlertDialogOverlay, AlertDialogContent, AlertDialogHeader, AlertDialogBody, AlertDialogFooter,
  background,
  space,
  Box,
  useToast,
  Image,
  IconButton,
  SimpleGrid,
} from '@chakra-ui/react';
import ReactPaginate from "react-paginate";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import {client} from "@/client"
import {GlobalContext, bearerToken, emptyUser} from "@/auth";
import {Schoolroom, User} from "@/pbgen/v1/main_pb"
import {
  Page,
  PageHeader,
  PageBody,
} from "@/components/layout"
import { CommentComponent } from "./CommentComponent";
import { CodeComponent } from "./CodeComponent";

import './ListForm.css';
import { MdSettings } from "react-icons/md";


const customTableStyles = css`
  th, td {
    padding-left: 0px;
    padding-right: 0px;
    padding-top: 0px;
    padding-bottom: 0px;
  }
`;

function studentStatus(status: string): string {
  switch (status) {
    case "trial": return "体験";
    case "enrolled": return "受講";
    case "absent": return "休会";
    case "event": return "イベント";
    default: return "不明";
  }
}

export const Students = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [vendors, setVendors] = useState<User[]>([]);
  const itemsPerPage = 50;
  const pageCount = Math.ceil(users.length / itemsPerPage);
  const [itemsOffset, setItemsOffset] = useState(0);
  const endOffset = itemsOffset + itemsPerPage
  const currentUsers = users.slice(itemsOffset, endOffset);
  const {globalState, setGlobalState} = useContext(GlobalContext);
  const navigate = useNavigate();
  const { vid, rid } = useParams();
  const [currentRole, setCurrentRole] = useState<string | undefined>(undefined)
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isAllDeleteOpen, onOpen: onAllDeleteOpen, onClose: onAllDeleteClose } = useDisclosure()
  const cancelAllDeleteRef = React.useRef<HTMLButtonElement | null>(null)
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [fileBase64, setFileBase64] = useState<string | null>(null);
  const toast = useToast();

  useEffect(() => {
    (async () => {
      if (! vid) {
        const resp = await client.getVendors({
          bearerToken: bearerToken(),
        });
        if (!resp.currentUser) {
          navigate("/login", {relative: "path"});
          return;
        }
        if (resp.vendors.length === 0) {
          navigate("/manage", {relative: "path"});
          return
        }
        const newVid = resp.vendors[0].id;
        navigate(`../vendors/${newVid}/students`, {relative: "path", replace: true});
        return
      }
      const resp = await client.getStudents({
        bearerToken: bearerToken(),
        optVendorId: vid,
        optSchoolroomId: rid ? parseInt(rid) : undefined,
      });
      setGlobalState({...globalState, currentUser: resp.currentUser ?? emptyUser()})
      if (! resp.currentUser) {
        navigate("/login", { relative: "path" });
        return;
      }
      setCurrentRole(resp.currentUser.optRole);
      setUsers(resp.students);
      if (resp.currentUser.optRole === "admin") {
        const resp = await client.getVendors({
          bearerToken: bearerToken(),
        });
        setVendors(resp.vendors);
      } else {
        const resp = await client.getUser({
          bearerToken: bearerToken(),
          id: vid,
        })
        if (! resp.user) {
          navigate("/manage", {relative: "path"});
          return
        }
        setVendors([resp.user])
      }
    })();
  }, [vid]) // eslint-disable-line react-hooks/exhaustive-deps

  const handlePageClick = (e: { selected: number }) => {
    const newOffset = (e.selected * itemsPerPage);
    setItemsOffset(newOffset);
  }

   // ファイルダウンロード処理
   const handleDownload = async () => {
    try {
      const resp = await client.getStudentsEx({
        bearerToken: bearerToken(),
        optVendorId: vid,
        optSchoolroomId: undefined,
      });

      // Blob に変換
      var blob = new Blob([resp.buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);

      // ダウンロード用のリンクを作成
      const a = document.createElement("a");
      a.href = url;
      a.download = "Students.xlsx"; // ダウンロードファイル名
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

    } catch (error) {
      console.error('Error:', error);
    }
  };
  
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {  
      const file = event.target.files[0];  
      setUploadFile(file);
    }
  };
  
  // ファイルアップロード処理
  const handleUpload = async () => {

    if (!uploadFile) {
      toast({
        title: "ファイルが選択されていません",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!uploadFile.name.endsWith(".xlsx")) {
      toast({
        title: "無効なファイル形式です",
        description: "Excel (.xlsx) ファイルを選択してください",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsUploading(true);

    // ファイルを base64 に変換
    const reader = new FileReader();
    reader.readAsDataURL(uploadFile);
    reader.onload = () => {
      const base64String = reader.result as string;
      //console.log("base64String", base64String);
      setFileBase64(base64String.split(",")[1]); // "data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64," の部分を除去
    };
    reader.onerror = (error) => {
      console.error("ファイルのエンコードに失敗しました", error);
      setIsUploading(false);
    };

  };

  // ファイルアップロード処理
  useEffect(() => {

    if (!fileBase64)  return

    async function fetchData() {

      if (fileBase64) {

        const resp = await client.saveStudentsEx({
          bearerToken: bearerToken(),
          vendorId: vid,
          data: fileBase64,
        });
        if (resp.error) {
  
          console.error("アップロードエラー:", resp.error);
          toast({
            title: "アップロードに失敗しました",
            status: "error",
            duration: 3000,
            isClosable: true,
          });
          setUploadFile(null);
        }
  
        setIsUploading(false);
        onClose()
        window.location.reload();
      }

    }
    fetchData();
  }, [fileBase64]);
  

  
  const calcAge = (birthday: string): number => {
    const divs = birthday.split("-");
    const birthYear = parseInt(divs[0]);
    const birthMonth = parseInt(divs[1]);
    const birthDay = parseInt(divs[2]);
    const now = new Date();
    const thisYearBirthday = new Date(now.getFullYear(), birthMonth - 1, birthDay);
    let age = now.getFullYear() - birthYear;
    if (now < thisYearBirthday) {
      age--;
    }
    return age;
  }

  // Returns “2006-01-02 15:04:05” in localtime
  const getDatetimeElement = (date: Date): JSX.Element => {

    return (
      <>
      <p>
      {
        (date.getFullYear() + "年" +
        (date.getMonth() + 1).toString().padStart(2, '0') + "月" +
        date.getDate().toString().padStart(2, '0') + "日")
      }
      </p>
      <p>
      {
        [ date.getHours().toString().padStart(2, '0'),
          date.getMinutes().toString().padStart(2, '0'),
          date.getSeconds().toString().padStart(2, '0')
        ].join(':')
      }
      </p>
      </>
    );
  }

  return <Page>
    <PageHeader title={"生徒一覧"}>
      <HStack padding={2}>

        {(currentRole === "admin") ? 
          <Select placeholder="事業者を選択" value={vid} onChange={(e) => navigate(`../../${e.target.value}/students`, {relative: "path", replace: true})}>
            {
              vendors.map((vendor) =>
                <option key={vendor.id} value={vendor.id}>{vendor.optName ?? ""}</option>
              )
            }
         </Select>
        : null }
  
        {(currentRole === "vendor") || (currentRole === "admin") ? 
          <Button px={9} onClick={() => {navigate("./new", { relative: "path" })}}>生徒新規登録</Button>
        : null }

        {(currentRole === "admin") ? 
          <>

            <Button px={9} onClick={ () => handleDownload() }>ファイル出力</Button>
            <Button px={9} onClick={ onOpen }>ファイル入力</Button>

            {/* モーダル */}
            <Modal isOpen={isOpen} onClose={onClose}>
              <ModalOverlay />
              <ModalContent>
                <ModalHeader>ファイルアップロード</ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                  <VStack spacing={3}>
                    <Box w="100%">
                      <Input type="file" accept=".xlsx" onChange={handleFileChange} />
                    </Box>
                    {uploadFile && <Text>選択されたファイル: {uploadFile.name}</Text>}
                  </VStack>
                </ModalBody>
                <ModalFooter>
                  <Button colorScheme="green" isLoading={isUploading} onClick={handleUpload} mr={3}>アップロード</Button>
                  <Button variant="ghost" onClick={onClose}>閉じる</Button>
                </ModalFooter>
              </ModalContent>
            </Modal>

            <Button px={9} onClick={async () => { onAllDeleteOpen() }}>全生徒削除</Button>

            <AlertDialog isOpen={isAllDeleteOpen} onClose={onAllDeleteClose} leastDestructiveRef={cancelAllDeleteRef}>
            <AlertDialogOverlay>
              <AlertDialogContent>
                <AlertDialogHeader>
                  全生徒の削除
                </AlertDialogHeader>
                <AlertDialogBody>
                  全生徒を本当に削除しますか？
                </AlertDialogBody>
                <AlertDialogFooter>
                  <Button ref={cancelAllDeleteRef} onClick={onAllDeleteClose}>
                    キャンセル
                  </Button>
                  <Button  ml={3} onClick={async () => {

                    const resp = await client.deleteAllUser({     
                      bearerToken: bearerToken(),
                      vendorId: vid,
                      role: "student",                      
                    });
                    if (resp.error) {
                      console.error("アップロードエラー:", resp.error);
                    }
                    window.location.reload();
                  }}>削除</Button>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialogOverlay>
            </AlertDialog>

          </>
        : null }

        {/* <Button onClick={() => {navigate("../..", { relative: "path" })}}>戻る</Button> */}
        {/*<Button isDisabled={window.history.length <= 2} onClick={() => {navigate(-1)}}>メニューに戻る</Button>*/}

        <Button px={9} as={Link} to="/manage">メニューに戻る</Button>
        <Button isDisabled={window.history.length <= 2} onClick={() => {navigate(-1)}}>戻る</Button>

        
      </HStack>
    </PageHeader>
    <PageBody >
      <VStack spacing={2} mt={0} mb={0} py={0}>

        <ReactPaginate 
          previousLabel="前へ"
          nextLabel="次へ"
          breakLabel="..."
          pageCount={pageCount} onPageChange={handlePageClick} className={'react-paginate'} />

          <Table className="list_table">
          <Thead>
            <Tr>
              <Th>ID</Th>
              <Th>教室名</Th>
              <Th>氏名/ふりがな/年齢</Th>
              <Th>ステータス</Th>
              <Th>最終ログイン日時</Th>
              {(currentRole === "teacher") || (currentRole === "admin") ? 
                <Th>ログインコード</Th>
              : null }
              <Th>連絡事項など</Th>
              {(currentRole === "teacher") || (currentRole === "admin") ? 
                <Th>教材追加</Th>
              : null }
              {(currentRole === "vendor") || (currentRole === "admin") ? 
              <Th>編集</Th>
              : null }
            </Tr>
          </Thead>
          <Tbody>
          {
            currentUsers.map((user) => <Tr key={user.id}>
              <Td>{user.id}</Td>
              <Td>{user.schoolroomName}</Td>
              <Td>
              <VStack align="start">
                {
                  (!!user.optFamilyName || !!user.optName) ? (
                    <>
                    <p>
                    {
                    (!!user.optFamilyName) ? (
                      <span className="list_table_name" >{user.optFamilyName ?? ""}　</span>
                      ) : null
                    }
                    {
                    (!!user.optName) ? (
                      <span className="list_table_name">{user.optName ?? ""}</span>
                      ) : null
                    }
                    </p>
                    </>
                  ) : null
                }

                {
                  (!!user.optFamilyNamePronunciation || !!user.optNamePronunciation) ? (
                    <>
                    <p>
                    {
                    (!!user.optFamilyNamePronunciation) ? (
                      <span>{user.optFamilyNamePronunciation ?? ""}　</span>
                      ) : null
                    }
                    {
                    (!!user.optNamePronunciation) ? (
                      <span>{user.optNamePronunciation ?? ""}</span>
                      ) : null
                    }
                    </p>
                    </>
                  ) : null
                }

                <span>({(user.optBirthday && user.optBirthday !== "0000-00-00") ? calcAge(user.optBirthday) : "—"})</span>
              
              </VStack>
              </Td>

              <Td>
              <p>{user.optStatus ? studentStatus(user.optStatus) : "—"}</p>
              <br />
              {user.optExpires && user.optExpiresAtRfc3339 ? <span>{"【有効期限】"}{getDatetimeElement(new Date(user.optExpiresAtRfc3339))}</span> : ""}
              </Td>

              <Td>{user.optLastLoginAtRfc3339 ? getDatetimeElement(new Date(user.optLastLoginAtRfc3339)) : "—"}</Td>

              {(currentRole === "teacher") || (currentRole === "admin") ? 
                <Td><CodeComponent user={user} /></Td>
              : null }

              <Td><CommentComponent user={user} /></Td>

              {(currentRole === "teacher") || (currentRole === "admin") ? 
                <td><Button isDisabled={false} onClick={() => { navigate(`./${user.id}/works`, { relative: "path" }) } } >教材</Button></td>
              : null }

              {(currentRole === "vendor") || (currentRole === "admin") ? 
                <Td><Button onClick={() => {navigate(`./${user.id}`, { relative: "path" })}}>編集</Button></Td>
              : null }
  
            </Tr>)
          }
          {
            [...Array(itemsPerPage - currentUsers.length)].map((_, i) => (<Tr key={`dummy-${i}`}>
              <Td>—</Td>
              <Td>—</Td>
              <Td>—</Td>
              <Td>—</Td>
              <Td>—</Td>
              {(currentRole === "teacher") || (currentRole === "admin") ? 
                <Td>—</Td>
              : null }
              <Td>—</Td>
              {(currentRole === "teacher") || (currentRole === "admin") ? 
                <Td>—</Td>
              : null }
              {(currentRole === "vendor") || (currentRole === "admin") ? 
                <Td>—</Td>
              : null }
              </Tr>))
          }
          </Tbody>
        </Table>
        {/*<ReactPaginate pageCount={pageCount} onClick={handlePageClick} />*/}
        <ReactPaginate 
          previousLabel="前へ"
          nextLabel="次へ"
          breakLabel="..."
          pageCount={pageCount} onPageChange={handlePageClick} className={'react-paginate'} />

      </VStack>
    </PageBody>
  </Page>
}


type FormData = {
  id: string;
  password: string;
  course: string;
  status: string;
  expiredate: Date;
  name: string;
  namePronunciation: string;
  familyName: string;
  familyNamePronunciation: string;
  note: string;
  opt_schoolroom_id: number | undefined;
  parentName: string;
  parentNamePronunciation: string;
  postalNumber: string;
  address1: string;
  address2: string;
  phoneNumber: string;
  email: string;
};

const getTimeZoneOffset = (): string => {
  const date = new Date();
  const options: Intl.DateTimeFormatOptions = { timeZoneName: 'longOffset' };
  const formatter = new Intl.DateTimeFormat('en-US', options);
  const parts = formatter.formatToParts(date);
  const timeZoneOffset = parts.find(part => part.type === 'timeZoneName')?.value;

  return timeZoneOffset || 'Unknown Time Zone';
}

export const Student = () => {
  const [formError, setFormError] = useState('');
  const [lastLoginAt, setLastLoginAt] = useState<string>('');
  const { vid, sid: id } = useParams();
  const {
    register: registerElement,
    control,
    handleSubmit: wrapSubmitHandlerWithFormValidator,
    formState: { errors: formErrors },
    setValue,
    getValues,
    setError,
    clearErrors,
  } = useForm<FormData>({});
  const { globalState, setGlobalState } = useContext(GlobalContext);
  const navigate = useNavigate();
  const [availableSchoolrooms, setAvailableSchoolrooms] = useState<Schoolroom[]>([]);
  const [schoolroomsLoaded, setSchoolroomsLoaded] = useState(false);
  const [birthday, setBirthday] = useState<Date | null>(null);
  const [expirationDate, setExpirationDate] = useState<Date | null>(null);
  const { isOpen: isErrorOpen, onOpen: onErrorOpen, onClose: onErrorClose } = useDisclosure()
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure()
  const cancelDeleteRef = React.useRef<HTMLButtonElement | null>(null)
  const [showPassword, setShowPassword] = useState(false);
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const { isOpen: isFinishOpen, onOpen: onFinishOpen, onClose: onFinishClose } = useDisclosure()
  const { isOpen: isImageSelOpen, onOpen: onImageSelOpen, onClose: onImageSelClose } = useDisclosure()
  const chrImages = [
    'chr00',
    'chr01',
    'chr02',
    'chr03',
    'chr04',
    'chr05',
  ];
  const [userImage, setUserImage] = useState<string>("chr00");

  useEffect(() => {
    (async () => {
      console.log("2dc5dfc");
      const resp2 = await client.getSchoolrooms({
        bearerToken: bearerToken(),
        optVendorId: vid,
      });
      setAvailableSchoolrooms(resp2.schoolrooms);
      setSchoolroomsLoaded(true);
    })();
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    (async () => {
      if (!schoolroomsLoaded) {
        return; // Schoolroomsが読み込まれていない場合は何もしない
      }
      console.log("069964e");
      if (id === "new") {
        const resp = await client.getLastId({
          bearerToken: bearerToken(),
          vendorId: vid,
        });
        setValue("id", (parseInt(resp.lastId) + 1).toString().padStart(resp.lastId.length, "0"))
        return;
      }
      const resp = await client.getUser({
        bearerToken: bearerToken(),
        id,
      });
      setGlobalState({...globalState, currentUser: resp.currentUser ?? emptyUser()})
      if (! resp.currentUser) {
        navigate("/login", { relative: "path" });
        return;
      }
      if (! resp.user) {
        navigate("..", { relative: "path", replace: true });
        return;
      }
      setValue("id", resp.user.id);
      setValue("password", "");
      setValue("course", resp.user.optCourse ?? "");
      if (resp.user.optStatus) {
        setValue("status", resp.user.optStatus ?? "");
      }
      const x = resp.user.optExpires !== undefined ? resp.user.optExpires : false
      if (resp.user.optExpires) {
        setExpirationDate(new Date(resp.user.optExpiresAtRfc3339 ?? ""));
      }
      if (resp.user.optBirthday && resp.user.optBirthday !== "0000-00-00") {
        // Date.parse() の指定でタイムゾーンを指定しない場合、年月日のみの文字列は UTC になり、時分まで指定するとローカルタイムになる謎仕様。以下で、ローカルタイムの 00:00 になる
        setBirthday(new Date(resp.user.optBirthday + "T00:00:00"));
      }
      setValue("name", resp.user.optName ?? "");
      setValue("namePronunciation", resp.user.optNamePronunciation ?? "");
      setValue("familyName", resp.user.optFamilyName ?? "");
      setValue("familyNamePronunciation", resp.user.optFamilyNamePronunciation ?? "");
      setValue("note", resp.user.optNote ?? "");
      setValue("opt_schoolroom_id", resp.user.optSchoolroomId ?? undefined);

      setValue("parentName", resp.user.optParentName ?? "");
      setValue("parentNamePronunciation", resp.user.optParentNamePronunciation ?? "");
      setValue("postalNumber", resp.user.optPostalNumber ?? "");
      setValue("address1", resp.user.optAddress1 ?? "");
      setValue("address2", resp.user.optAddress2 ?? "");
      setValue("phoneNumber", resp.user.optPhoneNumber ?? "");
      setValue("email", resp.user.optEmail ?? "");
      setUserImage(resp.user.optImageId || "chr00");

      setLastLoginAt(resp.user.optLastLoginAtRfc3339 ?? "");
    })();
  }, [availableSchoolrooms]) // eslint-disable-line react-hooks/exhaustive-deps
  if (! id) {
    navigate("..", { relative: "path", replace: true});
    return <></>;
  }

  const handleStatusChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const value = event.target.value;
    //console.log("Selected Value on Change:", value);

    if (value === "absent") {
      setExpirationDate(null);
    }
    if (((value === "trial") || (value === "event")) && (expirationDate === null)) {
      setError("expiredate", {
        type: "manual",
        message: "有効期限を設定してください。",
      });
    } else {
      clearErrors("expiredate");
    }
  };

  const togglePasswordVisibility = () => {
    setIsPasswordVisible(!isPasswordVisible); // 表示状態を切り替える
  };

  const handleOk = () => {
    navigate(-1);
  };

  const handleImageSelect = (src: string) => {
    setUserImage(src);
    onImageSelClose(); // 選んだら閉じる（必要に応じて省略可）
  };

  const submitIfValid = wrapSubmitHandlerWithFormValidator(async (data) => {
    //console.log(data)

    const user = new User({
      optVendorId: vid,
      id: data.id,
      optPassword: data.password,
      optCourse: data.course,
      optStatus: data.status,

      optName: data.name,
      optFamilyName: data.familyName,
      optNamePronunciation: data.namePronunciation,
      optFamilyNamePronunciation: data.familyNamePronunciation,
      optBirthday: birthday ? (date => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`)(birthday) : "0000-00-00",

      optParentName:  data.parentName,
      optParentNamePronunciation:  data.parentNamePronunciation,
      optPostalNumber:  data.postalNumber,
      optAddress1:  data.address1,
      optAddress2:  data.address2,
      optPhoneNumber:  data.phoneNumber,
      optEmail:  data.email,

      optExpires: expirationDate != null,
      optExpiresAtRfc3339: expirationDate?.toISOString() ?? undefined,

      optNote: data.note,
      optSchoolroomId: data.opt_schoolroom_id ?? undefined,

      optImageId: userImage,
    })
    console.log(user)

    const resp = await client.saveUser({
      bearerToken: bearerToken(),
      user: user,
      newUser: ("new" === id),
    });
    if (resp.error) {
      setFormError(resp.error.message);
      onErrorOpen();
      return;
    } else {
      onFinishOpen();
    }
/*    
    if (id === "new") {
      navigate(`../${data.id}`, {relative: "path", replace: true})
    }
 */   
  });
  return <Page>
    <PageHeader title={("new" === id)? "生徒新規登録": "生徒編集"}>
    <HStack padding={2}>
        <Button onClick={submitIfValid}>{(id === "new")? "登録": "保存"}</Button>
        <Button isDisabled={window.history.length <= 2} onClick={() => {navigate(-1)}}>戻る</Button>

        <AlertDialog isOpen={isDeleteOpen} onClose={onDeleteClose} leastDestructiveRef={cancelDeleteRef}>
          <AlertDialogOverlay>
            <AlertDialogContent>
              <AlertDialogHeader>
                生徒の削除
              </AlertDialogHeader>
              <AlertDialogBody>
                生徒「{id}」を本当に削除しますか？
              </AlertDialogBody>
              <AlertDialogFooter>
                <Button ref={cancelDeleteRef} onClick={onDeleteClose}>
                  キャンセル
                </Button>
                <Button  ml={3} onClick={async () => {
                  const resp = await client.deleteUser({
                    bearerToken: bearerToken(),
                    id,
                  });
                  if (resp.error) {
                    setFormError(resp.error.message);
                    onErrorOpen();
                    return;
                  }
                  navigate("..", { relative: "path", replace: true})
                }}>削除</Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialogOverlay>
        </AlertDialog>

        {("new" !== id) ? 
          <Button onClick={async () => {onDeleteOpen()}}>削除</Button>
        : null }

        </HStack>
    </PageHeader>

    <PageBody>
      <VStack className="edit_form" spacing={4} align="start">

        <HStack align={"flex-start"}>

          <Box position="relative" w="140px" h="140px" marginRight={3}>
            <Image src={`/ap/img/${userImage}.png`}
              alt="キャラクター"
              objectFit="cover"
              w="100%" h="100%" 
              borderRadius="md"
            />
            <IconButton
              icon={<MdSettings />}
              aria-label="えらぶ"
              position="absolute"
              bottom="1"
              right="1"
              size="xs"
              colorScheme="teal"
              onClick={onImageSelOpen}
            />
          </Box>
          <Modal isOpen={isImageSelOpen} onClose={onImageSelClose} isCentered size="xl">
            <ModalOverlay />
            <ModalContent w="fit-content" minW="auto" maxW="unset" >
              <ModalHeader>キャラクターをえらぼう</ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                <SimpleGrid columns={[2, 3]} spacing={2}>
                {chrImages.map((src) => (
                  <Box
                    key={src}
                    w="fit-content"
                    h="fit-content"
                    border={userImage === src ? '3px solid #ff0000' : '1px solid #ccc'}
                    borderRadius="md"
                    overflow="hidden"
                    cursor="pointer"
                    onClick={() => handleImageSelect(src)}
                    transition="all 0.2s"
                    _hover={{ borderColor: '#3182ce' }}
                  >
                    <Image src={`/ap/img/${src}.png`} alt={src} boxSize="80px" objectFit="contain" />
                  </Box>
                  ))}
                </SimpleGrid>
              </ModalBody>
            </ModalContent>
          </Modal>

          <VStack justify={"center"} align={"flex-start"} spacing={4}>

            <HStack>
              <FormControl isInvalid={Boolean(formErrors.id)}>

                <FormLabel><span>ID</span></FormLabel>

                <Input className="edit_form_short" disabled autoComplete={"off"} {...registerElement("id", {})} />
{/*
          <Input isDisabled={("new" !== id)} autoComplete={"off"} {...registerElement("id", {})} />
            <FormErrorMessage>{ formErrors.id?.message }</FormErrorMessage>
*/}
              </FormControl>
            </HStack>

            <HStack>
            {
              ("new" === id) ? (
                <>

                <VStack align="flex-start">
                  <FormControl isInvalid={Boolean(formErrors.password)} isRequired>
                    <FormLabel 
                      requiredIndicator = {
                        <span style={{ color: "white", fontSize: "70%", backgroundColor:"#df6694", marginLeft: "4px",}}>✱必須項目</span>
                      }>
                      <span>パスワード</span>
                    </FormLabel>
                    <Input
                      className="edit_form_middle"
                      type={showPassword ? "text" : "password"} 
                      placeholder=""
                      autoComplete={"new-password"}
                      {...registerElement("password", 
                        {
                          required: "パスワードは必須項目です。",
                          pattern: {
                            value: /^(?=.*\d).{8,}$/,
                            message: "パスワードは数字を含む8文字以上の半角文字を設定してください。",
                          },
                        }
                      )} />
                    <FormErrorMessage>{ formErrors.password?.message }</FormErrorMessage>
                  </FormControl>

                  <Checkbox fontSize="60%" m={0} isChecked={showPassword} onChange={() => setShowPassword(!showPassword)}>パスワードを見る</Checkbox>
                </VStack>

                <FormControl>
                  <div className="edit_form_summary">新規登録時はパスワードは必須項目です。<br />数字を含む8文字以上の半角文字を設定してください。</div>
                </FormControl>

                </>
              ) : (
                <>
                <HStack display={isPasswordVisible ? "flex" : "none"}>
                  <VStack align="flex-start">
                    <FormControl isInvalid={Boolean(formErrors.password)}>
                      <FormLabel><span>パスワード</span></FormLabel>
                      <Input
                        className="edit_form_middle"
                        type={showPassword ? "text" : "password"} 
                        placeholder=""
                        autoComplete={"new-password"}
                        {...registerElement("password", 
                          {
                            pattern: {
                              value: /^(?=.*\d).{8,}$/,
                              message: "パスワードは数字を含む8文字以上の半角文字を設定してください。",
                            },
                          }
                        )} />
                      <FormErrorMessage>{ formErrors.password?.message }</FormErrorMessage>
                    </FormControl>
                    <Checkbox fontSize="60%" m={0} isChecked={showPassword} onChange={() => setShowPassword(!showPassword)}>パスワードを見る</Checkbox>
                  </VStack>

                  <FormControl>
                    <div className="edit_form_summary">新しいパスワードを入力してください。<br />数字を含む8文字以上の半角文字を設定してください。</div>
                  </FormControl>
                </HStack>

                <HStack display={isPasswordVisible ? "none" : "flex"}>
                  <FormControl>
                    <Button onClick={togglePasswordVisibility}>パスワードの変更</Button>
                  </FormControl>
                  <FormControl>
                    <div className="edit_form_summary">パスワードを変更する際に、このボタンをクリックしてください。</div>
                  </FormControl>
                </HStack>
                </>
              )
            }
            </HStack>
          </VStack>

        </HStack>

        <Divider />

        <HStack>

          <FormControl isInvalid={Boolean(formErrors.opt_schoolroom_id)}>
            <FormLabel><span>教室</span></FormLabel>
            <Select
              className="edit_form_select"
              placeholder="教室を選択" {...registerElement("opt_schoolroom_id", { valueAsNumber: true })}>
              {
              availableSchoolrooms.map((schoolroom) =>
                <option key={schoolroom.id} value={schoolroom.id}>{schoolroom.optName ?? "?"}</option>
              )
            }
            </Select>
            <FormErrorMessage>{ formErrors.opt_schoolroom_id?.message }</FormErrorMessage>
          </FormControl>

          <FormControl isInvalid={Boolean(formErrors.course)}>
            <FormLabel><span>コース</span></FormLabel>
            <Input className="edit_form_long" {...registerElement("course", {})} />
            <FormErrorMessage>{ formErrors.course?.message }</FormErrorMessage>
          </FormControl>

        </HStack>

        <HStack>

          <FormControl isInvalid={ Boolean(formErrors.status) }>
            <FormLabel><span>ステータス</span></FormLabel>
            <Select
              className="edit_form_select"
              defaultValue="enrolled" 
              {...registerElement("status", {
                onChange: handleStatusChange, // onChangeイベントをフック
              })}
              >
              {/* todo: ループで記述 */}
              <option key={"trial"} value={"trial"}>{studentStatus("trial")}</option>
              <option key={"enrolled"} value={"enrolled"}>{studentStatus("enrolled")}</option>
              <option key={"absent"} value={"absent"}>{studentStatus("absent")}</option>
              {/* <option key={"event"} value={"event"}>{studentStatus("event")}</option> */}
            </Select>
            <FormErrorMessage>{ formErrors.status?.message }</FormErrorMessage>
          </FormControl>

          <FormControl isInvalid={ Boolean(formErrors.expiredate) }>
            <FormLabel><span>有効期限</span></FormLabel>

            <DatePicker 
              selected={expirationDate}
              onChange={(expirationDate: Date) => {
                setExpirationDate(expirationDate);
                const status = getValues("status");
                if (((status === "trial") || (status === "event")) && (expirationDate === null)) {
                  setError("expiredate", {
                    type: "manual",
                    message: "有効期限を設定してください。",
                  });
                } else {
                  clearErrors("expiredate");
                }
              }}
              selectsEnd
              placeholderText="無期限"
              customInput={React.createElement(forwardRef<HTMLInputElement, InputProps>((props, ref) => (<Input width="340px" height="50px" fontSize="lg" ref={ref} {...props}/>)))}
              showTimeSelect
              dateFormat="yyyy-MM-dd HH:mm:ss"
              popperPlacement="right"
              timeFormat="HH:mm"
              strictParsing={true}
            />


            <FormErrorMessage>{ formErrors.expiredate?.message }</FormErrorMessage>
          </FormControl>

          <FormControl >
            <div className="edit_form_summary">
              有効期限が設定されている場合、有効期限を過ぎるとログインできなくなります。<br />
              ステータスが「体験」と「イベント」のときは、<br />必ず有効期限を設定してください。
            </div>
          </FormControl>

        </HStack>

        <Divider />

        <VStack align="start">

          <HStack justify="flex-start">
            <FormControl isInvalid={ Boolean(formErrors.familyName) } isRequired w="fit-content">
              <FormLabel 
                requiredIndicator = {
                  <span style={{ color: "white", fontSize: "70%", backgroundColor:"#df6694", marginLeft: "4px",}}>✱必須項目</span>
                }>
                 <span>姓</span>
              </FormLabel>
              <Input className="edit_form_middle" {...registerElement("familyName", {
                required: "姓は必須項目です。"
              })} />
              <FormErrorMessage>{ formErrors.familyName?.message }</FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={ Boolean(formErrors.name) }>
              <FormLabel><span>名</span></FormLabel>
              <Input className="edit_form_middle" {...registerElement("name", {})} />
              <FormErrorMessage>{ formErrors.name?.message }</FormErrorMessage>
            </FormControl>

            <FormControl>
              <div className="edit_form_summary">
                姓は必須項目です。
              </div>
            </FormControl>
          </HStack>

          <HStack>
            <FormControl isInvalid={ Boolean(formErrors.familyNamePronunciation) }>
              <FormLabel><span>姓（かな）</span></FormLabel>
              <Input className="edit_form_middle" {...registerElement("familyNamePronunciation", {})} />
              <FormErrorMessage>{ formErrors.familyNamePronunciation?.message }</FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={ Boolean(formErrors.namePronunciation) }>
              <FormLabel><span>名（かな）</span></FormLabel>
              <Input className="edit_form_middle" {...registerElement("namePronunciation", {})} />
              <FormErrorMessage>{ formErrors.namePronunciation?.message }</FormErrorMessage>
            </FormControl>
          </HStack>

          <FormControl width="100%"> 
            <FormLabel><span>誕生日</span></FormLabel>          
            <DatePicker
              selected={birthday}
              onChange={(birthday: Date) => {
                setBirthday(birthday);
              }}
              placeholderText="未設定"
              customInput={React.createElement(forwardRef<HTMLInputElement, InputProps>((props, ref) => (<Input width="260px" height="50px" fontSize="lg" ref={ref} {...props}/>)))}
              strictParsing={true} // 全部入力した後で確定する。やや不具合あり // customInput lost the focus after entering one or max 2 characters · Issue #2051 · Hacker0x01/react-datepicker https://github.com/Hacker0x01/react-datepicker/issues/2051
              dateFormat="yyyy-MM-dd"
              popperPlacement="right"
              // openToDate={new Date("2000-01-01")}
              showYearDropdown
              minDate={new Date("1900-01-01")}
              maxDate={new Date()}
              yearDropdownItemNumber={100}
              showMonthDropdown
            />
          </FormControl>

        </VStack>

        <Divider />

        <HStack>
          <FormControl isInvalid={ Boolean(formErrors.parentName) }>
            <FormLabel><span>保護者氏名</span></FormLabel>
            <Input className="edit_form_long" {...registerElement("parentName", {})} />
            <FormErrorMessage>{ formErrors.parentName?.message }</FormErrorMessage>
         </FormControl>
        </HStack>

        <HStack>
          <FormControl isInvalid={ Boolean(formErrors.parentNamePronunciation) }>
            <FormLabel><span>保護者氏名（かな）</span></FormLabel>
            <Input className="edit_form_long" {...registerElement("parentNamePronunciation", {})} />
            <FormErrorMessage>{ formErrors.parentNamePronunciation?.message }</FormErrorMessage>
          </FormControl>
        </HStack>

        <HStack>
          <FormControl isInvalid={ Boolean(formErrors.postalNumber) }>
            <FormLabel><span>郵便番号</span></FormLabel>
            <Input className="edit_form_short" 
              type="number"
              {...registerElement("postalNumber", 
                {
                  pattern: {
                    value: /^\d{7}$/,
                    message: "郵便番号はハイフン'-'無し半角数字7桁で入力してください。",
                  },
                }
              )} />

            <FormErrorMessage>{ formErrors.postalNumber?.message }</FormErrorMessage>
          </FormControl>

          <FormControl >
            <div className="edit_form_summary">
              郵便番号はハイフン'-'無し半角数字7桁で入力してください。<br />
              例）1020074
           </div>
          </FormControl>
        </HStack>

        <HStack>
          
          <FormControl isInvalid={ Boolean(formErrors.address1) }>
            <FormLabel><span>住所１</span></FormLabel>
            <Input className="edit_form_long" {...registerElement("address1", {})} />
            <FormErrorMessage>{ formErrors.address1?.message }</FormErrorMessage>
          </FormControl>

          <FormControl >
            <div className="edit_form_summary">
              都道府県・市区町村・町域・番地で入力してください。<br />
              例）東京都千代田区九段南1-5-6
            </div>
          </FormControl>
        </HStack>

        <HStack>

          <FormControl isInvalid={ Boolean(formErrors.address2) }>
            <FormLabel><span>住所２</span></FormLabel>
            <Input className="edit_form_long" {...registerElement("address2", {})} />
            <FormErrorMessage>{ formErrors.address2?.message }</FormErrorMessage>
          </FormControl>

          <FormControl >
            <div className="edit_form_summary">
              建物名・マンション名・部屋番号等を入力してください。<br />
              例）りそな九段ビル5F
            </div>
          </FormControl>

        </HStack>

        <HStack>
          <FormControl isInvalid={ Boolean(formErrors.phoneNumber) }>
            <FormLabel><span>電話番号</span></FormLabel>
            <Input className="edit_form_middle"
            type="string"
            {...registerElement("phoneNumber", 
              {
                pattern: {
                  value: /^\(?\d{1,4}\)?-?\(?\d{1,4}-?\)?\d{4}$/,
                  message: "電話番号を半角文字で入力してください。",
                },
              }
            )} />
            <FormErrorMessage>{ formErrors.phoneNumber?.message }</FormErrorMessage>
          </FormControl>

          <FormControl >
            <div className="edit_form_summary">
              電話番号を半角文字で入力してください。<br />
              例）03-6869-2414
            </div>
          </FormControl>

        </HStack>

        <HStack>
          <FormControl isInvalid={ Boolean(formErrors.email) }>
            <FormLabel><span>メールアドレス</span></FormLabel>
            <Input className="edit_form_long" 
              {...registerElement("email", {
                pattern: {
                  value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                  message: "メールアドレスを半角文字で入力してください。",
                },
              }
            )} 
            />
            <FormErrorMessage>{ formErrors.email?.message }</FormErrorMessage>
          </FormControl>

          <FormControl >
            <div className="edit_form_summary">
              メールアドレスを半角文字で入力してください。<br />
              例）info@yuber.jp
            </div>
          </FormControl>

        </HStack>

        <Divider />

        <FormControl isInvalid={Boolean(formErrors.note)}>
          <FormLabel><span>備考</span></FormLabel>
          <Textarea size="lg" {...registerElement("note") } />
          <FormErrorMessage>{ formErrors.note?.message }</FormErrorMessage>
        </FormControl>

        <Modal isOpen={isFinishOpen} onClose={onFinishClose}>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>保存</ModalHeader>
            <ModalCloseButton />
            <ModalBody>生徒情報を保存しました。</ModalBody>
            <ModalFooter>
              <Button colorScheme='blue' mr={3} onClick={handleOk}>OK</Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
        
        <Modal isOpen={isErrorOpen} onClose={onErrorClose}>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Error</ModalHeader>
            <ModalCloseButton />
            <ModalBody>{formError}</ModalBody>
            <ModalFooter>
              <Button colorScheme='blue' mr={3} onClick={onErrorClose}>OK</Button>
            </ModalFooter>
          </ModalContent>
        </Modal>


        {
          // // Alert - Chakra UI https://chakra-ui.com/docs/components/alert
          // formError && <Alert status="error">
          //       <AlertIcon />
          //       <AlertTitle>{formError}</AlertTitle>
          //   {/*<AlertDescription>{formError}</AlertDescription>*/}
          //   </Alert>
        }
      </VStack>
    </PageBody>
  </Page>
}
